/*
Usage: 
<skio-cart-upsell item='{{ item | json | escape }}' line='{{ forloop.index }}'></skio-cart-upsell>
<script src="{{ 'skio-cart-upsell.js' | asset_url }}" type="module"></script>
May want to include above module globally if element is used in cart drawer
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

const skioCartStyles = css`
  .skio-cart-upsell {
    margin: 0;
    max-width: 100%;
    padding: 2px 10px;
    background: transparent;
    border: 1px solid #373c00;
    border-radius: 20px;
    cursor: pointer;
    outline: none;
    font-size: 12px;
    line-height: 1.2;
    font-family: RegolaMedium, sans-serif;
    color: #373c00;
  }

  .skio-cart-upgrade {
    margin: 0;
    color: #373c00;
    border: 1px solid #373c00;
    background: #faecd6;
    outline: none;
    cursor: pointer;
    padding: 5px 12px;
    border-radius: 24px;
    transition:
      color 0.25s,
      background-color 0.25s,
      border-color 0.25s;
    font-size: 10px;
    line-height: 1;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-family: RegolaBold, sans-serif;
  }

  .skio-cart-upgrade:hover {
    background: #373c00;
    color: white;
    border: 1px solid transparent;
  }
`;

export class SkioCartUpsell extends LitElement {
  static styles = skioCartStyles;

  static properties = {
    item: { type: Object },
    line: { type: Number },

    product: { type: Object },
    selectedVariant: { type: Object },
    key: { type: String },

    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    discount_format: { type: String },

    moneyFormatter: {},

    currency: { type: String },
  };

  constructor() {
    super();

    this.item = null;
    this.line = null;

    this.product = null;
    this.selectedVariant = null;

    this.skioSellingPlanGroups = null;
    this.availableSellingPlanGroups = null;

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.discountFormat = 'percent';

    this.currency = 'USD';
    this.moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
    });
  }

  upgradeButton = () => {
    if (this.availableSellingPlanGroups?.length > 0) {
      const discountPercent = this.discount(this.availableSellingPlanGroups[0].selected_selling_plan).percent;
      return html`
        <button
          class="skio-cart-upgrade"
          type="button"
          @click=${() => this.selectSellingPlan(this.availableSellingPlanGroups[0].selected_selling_plan.id)}
        >
          Upgrade to a Subscription ${discountPercent !== '0%' ? html`& Save ${discountPercent}` : ''}
        </button>
      `;
    } else {
      return html``;
    }
  };

  changeFrequency = () => {
    return html`
      <select
        class="skio-cart-upsell"
        skio-cart-upsell="${this.key}"
        @change=${(e) => this.selectSellingPlan(e.target.value)}
      >
        ${!this.product.requires_selling_plan
          ? html`
              <optgroup label="One Time Purchase">
                <option value="">One-time</option>
              </optgroup>
            `
          : ''}
        ${this.availableSellingPlanGroups
          ? this.availableSellingPlanGroups.map(
              (group, index) => html`
                <optgroup label="${group.name} (Save ${this.discount(group.selected_selling_plan).percent})">
                  ${group
                    ? group.selling_plans.map((selling_plan) => {
                        // Use cart item's selling plan name if this is the currently selected plan
                        const isSelected = selling_plan.id == this.selectedSellingPlan?.id;
                        const displayName =
                          isSelected && this.item.selling_plan_allocation?.selling_plan?.name
                            ? this.item.selling_plan_allocation.selling_plan.name
                            : selling_plan.name;

                        return html`
                          <option value="${selling_plan.id}" .selected=${isSelected}>${displayName}</option>
                        `;
                      })
                    : ''}
                </optgroup>
              `,
            )
          : ''}
      </select>
    `;
  };

  render() {
    if (!this.item || !this.product || !this.selectedVariant) return;
    return this.selectedSellingPlan ? this.changeFrequency() : this.upgradeButton();
  }

  updated = (changed) => {
    if (changed.has('item') && this.item) {
      this.fetchProduct(this.item.handle);
    }

    if (changed.has('product') && this.product) {
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) =>
          selling_plan_group.app_id === 'SKIO' &&
          !selling_plan_group.name.toLowerCase().includes('hidden-') &&
          !selling_plan_group.name.toLowerCase().includes('dynamic box subscription'),
      );

      this.selectedVariant = this.product.variants.find((variant) => variant.id == this.item.variant_id);

      if (this.item.selling_plan_allocation) {
        this.selectedSellingPlanGroup = this.skioSellingPlanGroups.find((group) =>
          group.selling_plans.find(
            (selling_plan) => selling_plan.id == this.item.selling_plan_allocation.selling_plan.id,
          ),
        );

        if (this.selectedSellingPlanGroup) {
          this.selectedSellingPlan = this.selectedSellingPlanGroup.selling_plans.find(
            (selling_plan) => selling_plan.id == this.item.selling_plan_allocation.selling_plan.id,
          );
        }
      }
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter((selling_plan_group) =>
        selling_plan_group.selling_plans.some((selling_plan) =>
          this.selectedVariant.selling_plan_allocations.some(
            (selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id,
          ),
        ),
      );

      this.availableSellingPlanGroups.forEach((group) => {
        group.selling_plans = group.selling_plans.filter((x) =>
          this.selectedVariant.selling_plan_allocations.find((y) => y.selling_plan_id == x.id),
        );
      });

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups.length) {
        //update each group with a default selected_selling_plan

        this.availableSellingPlanGroups.forEach((group) => {
          group.selected_selling_plan = group.selling_plans[0];
        });
      }
    }

    if (changed.has('selectedSellingPlan') && this.product) {
      this.updateLineItem();
    }
  };

  log = (...args) => {
    // Logging disabled for production
    // args.unshift('%c[skio cart upsell]', 'color: #8770f2;');
    // console.log.apply(console, args);
  };

  error = (...args) => {
    // Error logging disabled for production
    // args.unshift('%c [skio cart upsell]', 'color: #ff0000');
    // console.error.apply(console, args);
  };

  getSectionsToRender() {
    return [
      {
        id: 'cart_form',
        section: document.getElementById('cart_form').dataset.id,
        selector: '.cart__item-list',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  updateLineItem() {
    if (!this.line || !this.item) return;
    if (this.item.selling_plan_allocation?.selling_plan?.id == this.selectedSellingPlan?.id) return;

    // Get sections to render from mini-cart
    const miniCart = document.querySelector('mini-cart');
    const sections = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];

    let data;

    if (this.selectedSellingPlan) {
      data = JSON.stringify({
        line: this.line,
        quantity: this.item.quantity,
        selling_plan: this.selectedSellingPlan ? this.selectedSellingPlan?.id : null,
        properties: {
          'Subscription Discount': this.discount(this.selectedSellingPlan).percent,
        },
        sections: sections,
        sections_url: window.location.pathname,
      });
    } else {
      data = JSON.stringify({
        line: this.line,
        quantity: this.item.quantity,
        selling_plan: null,
        properties: {
          'Subscription Discount': null,
        },
        sections: sections,
        sections_url: window.location.pathname,
      });
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data,
    })
      .then((response) => response.text())
      .then((cart) => {
        const parsedCart = JSON.parse(cart);

        if (Shopify.theme.jsCart !== 'undefined' && window.location.href.includes('/cart')) {
          Shopify.theme.jsCart.updateView(parsedCart, this.line);
        }

        if (typeof Shopify.theme.jsAjaxCart !== 'undefined') {
          Shopify.theme.jsAjaxCart.updateView();
        }

        // Update mini-cart sections immediately (Skio has priority for cart data updates)
        const miniCart = document.querySelector('mini-cart');

        if (miniCart && miniCart.renderContents && parsedCart.sections) {
          miniCart.renderContents(parsedCart);

          // Notify GWP that Skio update is complete so GWP can do final check
          if (window.gwpManager) {
            window.gwpManager.schedulePostSkioUpdate();
          }
        } else {
          // If no sections available, force a mini-cart refresh
          this.forceMiniCartRefresh();
        }

        const cartUpdatedEvent = new CustomEvent('cartUpdated', {
          detail: {
            cart: parsedCart,
            line: this.line,
            source: 'skio-cart-upsell',
          },
        });
        document.dispatchEvent(cartUpdatedEvent);
      });
  }

  /**
   * Forces a mini-cart refresh when sections data is not available
   * This is a fallback for edge cases where /cart/change.js doesn't return sections
   */
  forceMiniCartRefresh() {
    const miniCart = document.querySelector('mini-cart');
    if (miniCart) {
      // Check if GWP is processing to avoid conflicts
      if (window.gwpManager?.isUpdatingUI) {
        return;
      }

      // Get fresh cart data and force refresh
      fetch('/cart.js')
        .then((response) => response.json())
        .then((freshCart) => {
          // Create mock sections response for renderContents
          const mockResponse = {
            ...freshCart,
            sections: {}, // Will trigger internal section fetching
          };

          if (miniCart.renderContents) {
            miniCart.renderContents(mockResponse);
          }

          // Notify GWP after fallback refresh
          if (window.gwpManager) {
            window.gwpManager.schedulePostSkioUpdate();
          }
        })
        .catch((error) => {
          // Error handling for mini-cart refresh failure
        });
    }
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(id) {
    if (!id) {
      this.selectedSellingPlanGroup = null;
      this.selectedSellingPlan = null;
      return;
    }

    let group = this.availableSellingPlanGroups.find((group) =>
      group.selling_plans.find((selling_plan) => selling_plan.id == id),
    );
    let selling_plan = group.selling_plans.find((x) => x.id == id);

    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          ' for variant ' +
          this.selectedVariant.id +
          ' from product ' +
          this.product.id +
          ' : ' +
          this.product.handle,
      );
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0);
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan) return { percent: '0%', amount: 0 };

    const price_adjustment = selling_plan.price_adjustments[0];
    const discount = { percent: '0%', amount: 0 };
    const price = this.selectedVariant.price;

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`;
        discount.amount = Math.round((price * price_adjustment.value) / 100.0);
        break;
      case 'fixed_amount':
        discount.percent = `${Math.round(((price_adjustment.value * 1.0) / price) * 100.0)}%`;
        discount.amount = price_adjustment.value;
        break;
      case 'price':
        discount.percent = `${Math.round((((price - price_adjustment.value) * 1.0) / price) * 100.0)}%`;
        discount.amount = price - price_adjustment.value;
        break;
    }

    return discount;
  }

  fetchProduct = async (handle) => {
    let productCache = window.sessionStorage.skioCartProductCache
      ? JSON.parse(window.sessionStorage.skioCartProductCache)
      : [];
    let cachedProduct = productCache ? productCache.find((product) => product.handle == handle) : null;

    if (cachedProduct) {
      this.product = cachedProduct;
    } else {
      await fetch(`/products/${handle}.js`)
        .then((response) => response.json())
        .then((response) => {
          this.product = response;
          productCache.push(response);
          window.sessionStorage.skioCartProductCache = JSON.stringify(productCache);
          this.requestUpdate();
        });
    }
  };
}

customElements.define('skio-cart-upsell', SkioCartUpsell);
